import { defineMessages } from 'react-intl';
import type { IntlShape, MessageDescriptor } from 'react-intl';
import {
  INICIATIVA_BADGES,
  INICIATIVA_BADGE_DEFAULT,
  type IniciativaBadgeKey,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';
import { SEO_TITLE_LENGTH } from 'volto-ministerio-publico-federal/widgets/CharCountWidget';
import { requireAltWhenOwnImage } from 'volto-ministerio-publico-federal/helpers/Image/altRequired';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_iniciativas_block',
    defaultMessage: 'Iniciativas e Projetos (capa)',
  },
  title: { id: 'mpf_iniciativas_title', defaultMessage: 'Título da faixa' },
  titleHelp: {
    id: 'mpf_iniciativas_title_help',
    defaultMessage: 'Em branco, usa "Iniciativas e Projetos".',
  },
  list: { id: 'mpf_iniciativas_list', defaultMessage: 'Cards' },
  listHelp: {
    id: 'mpf_iniciativas_list_help',
    defaultMessage:
      'A ordem é a posição no mosaico: o primeiro card ocupa a coluna alta da esquerda e os dois seguintes empilham à direita. Cards além do terceiro não aparecem.',
  },
  item: { id: 'mpf_iniciativas_item', defaultMessage: 'Card' },
  itemBadge: { id: 'mpf_iniciativas_item_badge', defaultMessage: 'Categoria' },
  itemBadgeHelp: {
    id: 'mpf_iniciativas_item_badge_help',
    defaultMessage: 'Define o texto da pílula e a cor do card.',
  },
  badgeProjeto: {
    id: 'mpf_iniciativas_badge_projeto',
    defaultMessage: 'Projeto',
  },
  badgeCampanha: {
    id: 'mpf_iniciativas_badge_campanha',
    defaultMessage: 'Campanha',
  },
  badgeIniciativa: {
    id: 'mpf_iniciativas_badge_iniciativa',
    defaultMessage: 'Iniciativa',
  },
  itemTitle: { id: 'mpf_iniciativas_item_title', defaultMessage: 'Título' },
  itemTitleHelp: {
    id: 'mpf_iniciativas_item_title_help',
    defaultMessage:
      'Até 60 caracteres é o que o Google costuma exibir sem cortar. Acima disso o título ainda aparece inteiro na capa.',
  },
  itemDescription: {
    id: 'mpf_iniciativas_item_description',
    defaultMessage: 'Descrição',
  },
  itemDescriptionHelp: {
    id: 'mpf_iniciativas_item_description_help',
    defaultMessage: 'Exibida em até três linhas, abaixo do título.',
  },
  itemHref: { id: 'mpf_iniciativas_item_href', defaultMessage: 'Conteúdo' },
  itemHrefHelp: {
    id: 'mpf_iniciativas_item_href_help',
    defaultMessage:
      'Destino do botão "Saiba mais": uma página do site ou um endereço externo. Ao escolher um conteúdo, o título é preenchido com o dele — título já editado não é sobrescrito. Sem destino, o botão não aparece.',
  },
  itemExternal: {
    id: 'mpf_iniciativas_item_external',
    defaultMessage: 'Abrir em nova aba',
  },
  fieldsetImage: {
    id: 'mpf_iniciativas_fieldset_image',
    defaultMessage: 'Imagem',
  },
  itemImage: { id: 'mpf_iniciativas_item_image', defaultMessage: 'Imagem' },
  itemImageHelp: {
    id: 'mpf_iniciativas_item_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL. Ela fica ao fundo, sob um véu na cor da categoria — prefira fotos com pouco texto.',
  },
  itemAlt: {
    id: 'mpf_iniciativas_item_alt',
    defaultMessage: 'Texto alternativo',
  },
  itemAltHelp: {
    id: 'mpf_iniciativas_item_alt_help',
    defaultMessage: 'Descreva o conteúdo da imagem para leitores de tela.',
  },
});

/**
 * Rótulo traduzido de cada categoria.
 *
 * O tipo amarra este mapa a `INICIATIVA_BADGES`: acrescentar uma categoria lá
 * sem um rótulo aqui quebra o build, em vez de virar uma opção sem tradução na
 * barra lateral.
 */
const BADGE_LABELS: Record<IniciativaBadgeKey, MessageDescriptor> = {
  projeto: messages.badgeProjeto,
  campanha: messages.badgeCampanha,
  iniciativa: messages.badgeIniciativa,
};

/** Opções do campo Categoria, na ordem em que as cores foram declaradas. */
const badgeChoices = (intl: IntlShape) =>
  (Object.keys(INICIATIVA_BADGES) as IniciativaBadgeKey[]).map((key) => [
    key,
    intl.formatMessage(BADGE_LABELS[key]),
  ]);

/** Campos de um card do mosaico. */
const iniciativaItemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['badge', 'title', 'description', 'href', 'external'],
    },
    {
      id: 'image',
      title: intl.formatMessage(messages.fieldsetImage),
      fields: ['image', 'alt'],
    },
  ],
  properties: {
    // A categoria não é só rótulo: ela escolhe a cor do véu e da pílula, que
    // vivem em `INICIATIVA_BADGES`/`_capa-overlay-cards.scss`.
    badge: {
      title: intl.formatMessage(messages.itemBadge),
      description: intl.formatMessage(messages.itemBadgeHelp),
      choices: badgeChoices(intl),
      default: INICIATIVA_BADGE_DEFAULT,
    },
    // Contador em vez de corte: o título aparece inteiro na capa, então o
    // tamanho é decisão editorial com aviso na hora de escrever. Ver
    // `widgets/CharCountWidget`.
    title: {
      title: intl.formatMessage(messages.itemTitle),
      description: intl.formatMessage(messages.itemTitleHelp),
      widget: 'charCount',
      recommendedLength: SEO_TITLE_LENGTH,
    },
    description: {
      title: intl.formatMessage(messages.itemDescription),
      description: intl.formatMessage(messages.itemDescriptionHelp),
      widget: 'textarea',
    },
    href: {
      title: intl.formatMessage(messages.itemHref),
      description: intl.formatMessage(messages.itemHrefHelp),
      widget: 'object_browser',
      mode: 'link',
      // Sem `image_scales`/`image_field` de propósito: aqui a foto é fundo de
      // um véu colorido, escolha editorial que a imagem do conteúdo
      // referenciado raramente atende — ver `getIniciativaImageProps`.
      selectedItemAttrs: ['@id', '@type', 'Title', 'Description'],
      allowExternals: true,
      initialPath: '/',
    },
    external: {
      title: intl.formatMessage(messages.itemExternal),
      type: 'boolean',
    },
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL.
    image: {
      title: intl.formatMessage(messages.itemImage),
      description: intl.formatMessage(messages.itemImageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.itemAlt),
      description: intl.formatMessage(messages.itemAltHelp),
    },
  },
  // `alt` só é obrigatório quando há imagem — o `schemaExtender` de `items`
  // cuida disso. Sem imagem no card, não há o que descrever.
  required: ['title'],
});

/**
 * Schema do bloco "Iniciativas e Projetos".
 *
 * Uma lista só, sem campo de posição: a ordem dos itens É o mosaico (ver
 * `layoutIniciativas`). `activeObject`/`setActiveObject` vêm do `Edit.tsx` e
 * entram no field spec de `items` — é o contrato que o `ObjectListWidget` do
 * core espera para aceitar um índice controlado de fora, e é o que faz o
 * lápis de um card abrir aquele item.
 */
export const mpfIniciativasSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'items'],
    },
  ],
  properties: {
    title: {
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.titleHelp),
      placeholder: 'Iniciativas e Projetos',
    },
    items: {
      title: intl.formatMessage(messages.list),
      description: intl.formatMessage(messages.listHelp),
      // Sem teto no widget: o `ObjectListWidget` do core não conhece
      // `maxLength`. O excedente é descartado no render
      // (`toIniciativaCards`) e o aviso está na ajuda do campo.
      widget: 'object_list',
      schema: iniciativaItemSchema({ intl }),
      schemaExtender: requireAltWhenOwnImage,
      activeObject,
      setActiveObject,
    },
  },
  required: [],
});

export default mpfIniciativasSchema;
